/**
 * Executor's Probate Checklist Generator
 * ------------------------------------------------------------------
 * Produces a branded, printable PDF checklist for executors.
 *
 * Two variants:
 *   - generateGenericChecklist({ stateData? })  -> universal lead magnet
 *   - generatePersonalizedChecklist(ctx)        -> estate-specific report
 *
 * Both share the same visual identity (logo header, brand palette, sectioned
 * checkboxes, footer). The personalized version pulls live state guidance,
 * tasks, assets, heirs, debts, contacts, etc., from the in-app context.
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// EstateProFiler brand palette
const BRAND_PRIMARY = [15, 61, 46];   // #0f3d2e dark green
const BRAND_ACCENT = [201, 164, 73];  // #c9a449 gold
const BRAND_TEXT = [26, 26, 26];      // near-black
const BRAND_MUTED = [120, 120, 120];  // mid grey
const BRAND_SUBTLE = [246, 247, 243]; // off-white

const PAGE_MARGIN = 14;

// Format helpers
const fmtDate = (d) => {
  if (!d) return '________________';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return d;
  }
};

const fmtMoney = (n) => {
  const num = parseFloat(n);
  if (Number.isNaN(num)) return '________________';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
};

/* ------------------------------------------------------------------
 * Header / Footer / Section primitives
 * ------------------------------------------------------------------ */

function drawCoverPage(pdf, title, subtitle, estateLine) {
  const w = pdf.internal.pageSize.getWidth();
  const h = pdf.internal.pageSize.getHeight();

  // Dark green band
  pdf.setFillColor(...BRAND_PRIMARY);
  pdf.rect(0, 0, w, h * 0.45, 'F');

  // Wordmark
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(22);
  pdf.setTextColor(255, 255, 255);
  pdf.text('EstateProFiler', w / 2, 28, { align: 'center' });

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(255, 255, 255);
  pdf.text('estateprofiler.com', w / 2, 35, { align: 'center' });

  // Title
  pdf.setFont('times', 'bold');
  pdf.setFontSize(30);
  pdf.setTextColor(255, 255, 255);
  pdf.text(title, w / 2, h * 0.25, { align: 'center', maxWidth: w - 40 });

  // Subtitle
  pdf.setFont('times', 'italic');
  pdf.setFontSize(14);
  pdf.setTextColor(...BRAND_ACCENT);
  pdf.text(subtitle, w / 2, h * 0.32, { align: 'center', maxWidth: w - 40 });

  // Estate / generic line below the band
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(12);
  pdf.setTextColor(...BRAND_TEXT);
  pdf.text(estateLine, w / 2, h * 0.52, { align: 'center', maxWidth: w - 30 });

  // Intro paragraph
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...BRAND_MUTED);
  const intro =
    'This checklist is a comprehensive guide for executors and personal ' +
    'representatives navigating probate. Use it to track every step from ' +
    'opening the estate through final distribution. Check items off as you ' +
    'complete them, and use the lines below each section for notes and dates.';
  pdf.text(intro, PAGE_MARGIN, h * 0.62, { maxWidth: w - PAGE_MARGIN * 2, align: 'left' });

  // Footer brand mark
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...BRAND_MUTED);
  pdf.text(`Generated ${new Date().toLocaleDateString()}`, w / 2, h - 14, { align: 'center' });
}

function drawPageHeader(pdf, sectionTitle) {
  const w = pdf.internal.pageSize.getWidth();
  // Top accent line
  pdf.setFillColor(...BRAND_PRIMARY);
  pdf.rect(0, 0, w, 1.5, 'F');

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(...BRAND_PRIMARY);
  pdf.text('EstateProFiler · Executor\'s Probate Checklist', PAGE_MARGIN, 9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...BRAND_MUTED);
  pdf.text(sectionTitle, w - PAGE_MARGIN, 9, { align: 'right' });
}

function drawPageFooter(pdf, pageNo, totalPages) {
  const w = pdf.internal.pageSize.getWidth();
  const h = pdf.internal.pageSize.getHeight();
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...BRAND_MUTED);
  pdf.text('© EstateProFiler · For educational purposes — not legal advice.', PAGE_MARGIN, h - 7);
  if (totalPages) {
    pdf.text(`Page ${pageNo} of ${totalPages}`, w - PAGE_MARGIN, h - 7, { align: 'right' });
  }
}

function sectionTitle(pdf, y, label) {
  const w = pdf.internal.pageSize.getWidth();
  pdf.setFillColor(...BRAND_PRIMARY);
  pdf.rect(PAGE_MARGIN, y, w - PAGE_MARGIN * 2, 9, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(255, 255, 255);
  pdf.text(label, PAGE_MARGIN + 4, y + 6.3);
  return y + 14;
}

function subTitle(pdf, y, label) {
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(10);
  pdf.setTextColor(...BRAND_PRIMARY);
  pdf.text(label, PAGE_MARGIN, y);
  pdf.setDrawColor(...BRAND_ACCENT);
  pdf.setLineWidth(0.4);
  pdf.line(PAGE_MARGIN, y + 1.2, PAGE_MARGIN + 60, y + 1.2);
  return y + 6;
}

function checkRow(pdf, y, text, opts = {}) {
  const { sub = null, status = null, lineWidth = 175 } = opts;
  // Checkbox
  pdf.setDrawColor(...BRAND_PRIMARY);
  pdf.setLineWidth(0.5);
  pdf.rect(PAGE_MARGIN, y - 3.5, 4, 4);
  if (status === 'done') {
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...BRAND_PRIMARY);
    pdf.text('X', PAGE_MARGIN + 1, y - 0.4);
  }
  // Primary line
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...BRAND_TEXT);
  const lines = pdf.splitTextToSize(text, lineWidth - 10);
  pdf.text(lines, PAGE_MARGIN + 7, y);
  let yEnd = y + lines.length * 4.6;
  // Sub-line
  if (sub) {
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...BRAND_MUTED);
    const subLines = pdf.splitTextToSize(sub, lineWidth - 10);
    pdf.text(subLines, PAGE_MARGIN + 7, yEnd + 1);
    yEnd += subLines.length * 4 + 1;
  }
  return yEnd + 2;
}

function ensureSpace(pdf, y, needed = 20) {
  const h = pdf.internal.pageSize.getHeight();
  if (y + needed > h - 18) {
    pdf.addPage();
    drawPageHeader(pdf, 'Executor\'s Probate Checklist');
    return 18;
  }
  return y;
}

function notesBlock(pdf, y, lines = 4) {
  pdf.setDrawColor(220, 220, 220);
  pdf.setLineWidth(0.2);
  for (let i = 0; i < lines; i++) {
    pdf.line(PAGE_MARGIN, y, pdf.internal.pageSize.getWidth() - PAGE_MARGIN, y);
    y += 6;
  }
  return y + 2;
}

/* ------------------------------------------------------------------
 * Section content builders
 * ------------------------------------------------------------------ */

const FIRST_WEEK_ITEMS = [
  { t: 'Locate the original will and any codicils.', s: 'Check fireproof safes, safe deposit boxes, attorneys\' offices.' },
  { t: 'Order 10–15 certified death certificates.', s: 'Required by banks, brokerages, the IRS, Social Security, life insurers.' },
  { t: 'Secure the decedent\'s home and personal property.', s: 'Change locks, redirect mail, take photos of valuable items.' },
  { t: 'Notify close family and beneficiaries verbally.', s: 'Formal written notice comes later.' },
  { t: 'Identify and contact any pre-arranged funeral / burial plans.', s: '' },
  { t: 'Cancel scheduled appointments and unnecessary subscriptions.', s: 'TV, streaming, gym memberships, magazine subscriptions.' },
  { t: 'Notify Social Security Administration of death.', s: 'Call 1-800-772-1213. SSA notifies Medicare automatically.' },
  { t: 'Notify VA, pension provider, or employer if applicable.', s: '' },
  { t: 'Locate financial records — bank statements, tax returns, insurance policies.', s: 'Last 3 years of returns at minimum.' },
  { t: 'Prepare to retain a probate attorney if the estate is complex.', s: 'Larger estates, multiple properties, business interests, or family disputes.' },
];

const TAX_FINANCE_ITEMS = [
  { t: 'Obtain a Federal Tax ID (EIN) for the estate from the IRS.', s: 'Required to open the estate bank account. Apply at irs.gov.' },
  { t: 'Open an estate checking account.', s: 'Use the EIN. All estate income and expenses flow through this account.' },
  { t: 'File the decedent\'s final personal income tax return (Form 1040).', s: 'Due April 15 of the year following death.' },
  { t: 'File estate income tax return (Form 1041) if estate generates income.', s: '' },
  { t: 'File federal estate tax return (Form 706) if estate exceeds the threshold.', s: '$13.61M in 2024; check current year.' },
  { t: 'File any state estate or inheritance tax return.', s: '' },
  { t: 'Update beneficiary designations on IRAs, 401(k)s, life insurance.', s: 'Even if the will controls, beneficiary designations override the will.' },
  { t: 'Close credit cards and revolving accounts.', s: '' },
];

const HEIR_NOTIFY_ITEMS = [
  { t: 'Send formal written notice of probate to each heir / beneficiary.', s: 'Required by state law within a specific window after appointment.' },
  { t: 'Mail a copy of the will (or its filing reference) to each heir.', s: '' },
  { t: 'Track delivery — certified mail with return receipt is best.', s: '' },
  { t: 'Provide each heir with your contact info as personal representative.', s: '' },
  { t: 'Hold or document any heir disclaimers (refusal of inheritance).', s: 'Disclaimers must be in writing and timely; consult counsel.' },
];

const DISTRIBUTION_ITEMS = [
  { t: 'Confirm court has approved final accounting before distributing.', s: 'Some states require approval; others allow direct distribution if all heirs sign waivers.' },
  { t: 'Obtain signed receipts and releases from each heir before distribution.', s: 'Protects you from later claims.' },
  { t: 'Distribute specific bequests first (jewelry, vehicles, named items).', s: '' },
  { t: 'Distribute residuary estate per the will or intestacy %.', s: '' },
  { t: 'Issue final 1099s if required (for distributions of estate income).', s: '' },
  { t: 'File final report and petition for discharge with the probate court.', s: 'Officially closes the estate and discharges your duties.' },
  { t: 'Retain all estate records for 7 years.', s: 'Tax records, court filings, signed receipts, communications with heirs.' },
];

function renderProbateSteps(pdf, y, steps, stateData) {
  y = sectionTitle(pdf, y, '1.  State-Specific Probate Filing Steps');
  if (stateData) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(...BRAND_MUTED);
    const info = `${stateData.name || ''} · ${stateData.statutes || ''}`;
    pdf.text(info, PAGE_MARGIN, y, { maxWidth: pdf.internal.pageSize.getWidth() - PAGE_MARGIN * 2 });
    y += 7;
    pdf.setFontSize(9);
    pdf.text(`Filing deadline: ${stateData.filing_deadline || 'N/A'}`, PAGE_MARGIN, y);
    y += 4.5;
    pdf.text(`Creditor claim period: ${stateData.creditor_claim_period || 'N/A'}`, PAGE_MARGIN, y);
    y += 4.5;
    pdf.text(`Inventory due: ${stateData.inventory_deadline || 'N/A'}`, PAGE_MARGIN, y);
    y += 7;
  }

  steps.forEach((step, idx) => {
    y = ensureSpace(pdf, y, 18);
    const num = step.order || idx + 1;
    y = checkRow(pdf, y, `${num}. ${step.title}`, { sub: step.description });
  });
  y += 2;
  y = ensureSpace(pdf, y, 30);
  y = subTitle(pdf, y, 'Notes — Probate Filing');
  y = notesBlock(pdf, y, 4);
  return y + 4;
}

function renderFirstWeek(pdf, y) {
  y = ensureSpace(pdf, y, 50);
  y = sectionTitle(pdf, y, '2.  Immediate First-Week Items');
  FIRST_WEEK_ITEMS.forEach((it) => {
    y = ensureSpace(pdf, y, 14);
    y = checkRow(pdf, y, it.t, { sub: it.s });
  });
  y = ensureSpace(pdf, y, 30);
  y = subTitle(pdf, y, 'Notes — First Week');
  y = notesBlock(pdf, y, 4);
  return y + 4;
}

function renderAssetsInventory(pdf, y, assets = [], debts = []) {
  y = ensureSpace(pdf, y, 50);
  y = sectionTitle(pdf, y, '3.  Asset & Debt Inventory Checklist');

  // Render as autoTable for clean tabular feel
  if (assets.length > 0) {
    y = subTitle(pdf, y, 'Assets');
    autoTable(pdf, {
      startY: y,
      head: [['□', 'Name', 'Category', 'Estimated Value', 'Status']],
      body: assets.map((a) => [
        '',
        a.name || '',
        a.category || '',
        fmtMoney(a.estimated_value),
        a.status || 'Identified',
      ]),
      theme: 'grid',
      headStyles: { fillColor: BRAND_PRIMARY, textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { cellWidth: 8, halign: 'center' } },
      margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    });
    y = pdf.lastAutoTable.finalY + 6;
  } else {
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(9);
    pdf.setTextColor(...BRAND_MUTED);
    pdf.text('Begin by inventorying every asset — bank accounts, real estate, vehicles, retirement, life insurance, personal property.', PAGE_MARGIN, y, { maxWidth: 180 });
    y += 10;
  }

  if (debts.length > 0) {
    y = ensureSpace(pdf, y, 30);
    y = subTitle(pdf, y, 'Outstanding Debts & Claims');
    autoTable(pdf, {
      startY: y,
      head: [['□', 'Creditor', 'Category', 'Amount', 'Priority', 'Status']],
      body: debts.map((d) => [
        '',
        d.creditor || d.name || '',
        d.category || '',
        fmtMoney(d.amount),
        d.priority || '',
        d.status || 'Pending',
      ]),
      theme: 'grid',
      headStyles: { fillColor: BRAND_PRIMARY, textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { cellWidth: 8, halign: 'center' } },
      margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    });
    y = pdf.lastAutoTable.finalY + 6;
  }
  return y;
}

function renderHeirsNotification(pdf, y, heirs = []) {
  y = ensureSpace(pdf, y, 50);
  y = sectionTitle(pdf, y, '4.  Heir Notification Checklist');
  HEIR_NOTIFY_ITEMS.forEach((it) => {
    y = ensureSpace(pdf, y, 14);
    y = checkRow(pdf, y, it.t, { sub: it.s });
  });

  if (heirs.length > 0) {
    y = ensureSpace(pdf, y, 30);
    y = subTitle(pdf, y, 'Heirs to Notify');
    autoTable(pdf, {
      startY: y,
      head: [['□', 'Name', 'Relation', 'Share %', 'Notified', 'Receipt Signed']],
      body: heirs.map((h) => [
        '',
        h.name || '',
        h.relation || '',
        h.residuary_percent != null ? `${h.residuary_percent}%` : '—',
        '☐',
        '☐',
      ]),
      theme: 'grid',
      headStyles: { fillColor: BRAND_PRIMARY, textColor: 255, fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { cellWidth: 8, halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' } },
      margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    });
    y = pdf.lastAutoTable.finalY + 4;
  }
  return y;
}

function renderCourtForms(pdf, y, forms = [], stateName) {
  y = ensureSpace(pdf, y, 50);
  y = sectionTitle(pdf, y, '5.  Court Forms Checklist');
  if (forms.length === 0) {
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(9);
    pdf.setTextColor(...BRAND_MUTED);
    pdf.text('Visit your state probate court website to download required forms.', PAGE_MARGIN, y, { maxWidth: 180 });
    return y + 10;
  }
  if (stateName) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(...BRAND_MUTED);
    pdf.text(`Required and commonly used forms in ${stateName}:`, PAGE_MARGIN, y);
    y += 6;
  }
  autoTable(pdf, {
    startY: y,
    head: [['□', 'Form', 'Description']],
    body: forms.map((f) => [
      '',
      f.name || f.id || '',
      f.description || '',
    ]),
    theme: 'grid',
    headStyles: { fillColor: BRAND_PRIMARY, textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: { 0: { cellWidth: 8, halign: 'center' } },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });
  return pdf.lastAutoTable.finalY + 4;
}

function renderTaxFinance(pdf, y) {
  y = ensureSpace(pdf, y, 50);
  y = sectionTitle(pdf, y, '6.  Tax & Financial Closures');
  TAX_FINANCE_ITEMS.forEach((it) => {
    y = ensureSpace(pdf, y, 14);
    y = checkRow(pdf, y, it.t, { sub: it.s });
  });
  y = ensureSpace(pdf, y, 30);
  y = subTitle(pdf, y, 'Notes — Tax & Financial Closures');
  y = notesBlock(pdf, y, 4);
  return y + 4;
}

function renderFinalDistribution(pdf, y) {
  y = ensureSpace(pdf, y, 50);
  y = sectionTitle(pdf, y, '7.  Final Distribution & Estate Closure');
  DISTRIBUTION_ITEMS.forEach((it) => {
    y = ensureSpace(pdf, y, 14);
    y = checkRow(pdf, y, it.t, { sub: it.s });
  });
  y = ensureSpace(pdf, y, 30);
  y = subTitle(pdf, y, 'Notes — Distribution & Closure');
  y = notesBlock(pdf, y, 4);
  return y + 4;
}

function renderTasksProgress(pdf, y, tasks = []) {
  if (tasks.length === 0) return y;
  y = ensureSpace(pdf, y, 50);
  y = sectionTitle(pdf, y, '8.  Your In-App Tasks');
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...BRAND_MUTED);
  pdf.text(`Live task list from EstateProFiler (${tasks.filter((t) => t.completed).length} of ${tasks.length} complete):`, PAGE_MARGIN, y);
  y += 5;
  autoTable(pdf, {
    startY: y,
    head: [['□', 'Task', 'Category', 'Due', 'Priority']],
    body: tasks.map((t) => [
      t.completed ? 'X' : '',
      t.title || '',
      t.category || '',
      t.due_date ? fmtDate(t.due_date) : '—',
      t.priority || 'medium',
    ]),
    theme: 'grid',
    headStyles: { fillColor: BRAND_PRIMARY, textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center', fontStyle: 'bold' },
      3: { cellWidth: 30 },
      4: { cellWidth: 22, halign: 'center' },
    },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });
  return pdf.lastAutoTable.finalY + 4;
}

function renderContacts(pdf, y, contacts = []) {
  if (contacts.length === 0) return y;
  y = ensureSpace(pdf, y, 50);
  y = sectionTitle(pdf, y, '9.  Professional Contacts');
  autoTable(pdf, {
    startY: y,
    head: [['Name', 'Role', 'Organization', 'Email', 'Phone']],
    body: contacts.map((c) => [
      c.name || '',
      c.role || '',
      c.organization || '',
      c.email || '',
      c.phone || '',
    ]),
    theme: 'grid',
    headStyles: { fillColor: BRAND_PRIMARY, textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
  });
  return pdf.lastAutoTable.finalY + 4;
}

/* ------------------------------------------------------------------
 * Pagination footer — runs at the end on every page
 * ------------------------------------------------------------------ */

function paginate(pdf) {
  const total = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    if (i > 1) drawPageHeader(pdf, 'Executor\'s Probate Checklist');
    drawPageFooter(pdf, i, total);
  }
}

/* ------------------------------------------------------------------
 * GENERIC checklist — no estate data
 * ------------------------------------------------------------------ */

export function generateGenericChecklist({ stateData = null, fileName = 'EstateProFiler_Executors_Probate_Checklist.pdf' } = {}) {
  const pdf = new jsPDF({ unit: 'mm', format: 'letter' });

  drawCoverPage(
    pdf,
    "The Executor's Probate Checklist",
    'A complete step-by-step guide for personal representatives',
    stateData
      ? `Tailored for ${stateData.name}`
      : 'Universal edition — applicable to executors in all 50 states'
  );

  // Body pages
  pdf.addPage();
  drawPageHeader(pdf, "Executor's Probate Checklist");

  let y = 18;

  // Generic baseline 11-step roadmap (matches in-app state guide structure)
  const baseSteps = [
    { order: 1, title: 'Locate and Review the Will', description: 'Find the original will and review its provisions.' },
    { order: 2, title: 'File Petition for Probate', description: 'File with the appropriate probate court along with the will and death certificate.' },
    { order: 3, title: 'Notify Heirs and Beneficiaries', description: 'Provide formal notice to all interested parties as required by state law.' },
    { order: 4, title: 'Obtain Letters Testamentary / of Administration', description: 'Court-issued letters grant your legal authority to act for the estate.' },
    { order: 5, title: 'Publish Notice to Creditors', description: 'Notify potential creditors per your state\'s newspaper-publication rules.' },
    { order: 6, title: 'Inventory and Appraise Assets', description: 'Identify, locate, and value all estate assets. File the inventory if required.' },
    { order: 7, title: 'Pay Debts and Claims', description: 'Pay valid claims in the order of priority set by state law.' },
    { order: 8, title: 'File Estate Tax Returns', description: 'Federal Form 706 if required, plus any state-level estate or inheritance tax.' },
    { order: 9, title: 'Prepare Final Accounting', description: 'Detailed report of all transactions, with proposed final distributions.' },
    { order: 10, title: 'Distribute Assets to Beneficiaries', description: 'Distribute per the will or intestacy laws after any required court approval.' },
    { order: 11, title: 'File Final Report and Close Estate', description: 'Petition for discharge formally ends your duties.' },
  ];

  y = renderProbateSteps(pdf, y, stateData?.steps || baseSteps, stateData);
  y = renderFirstWeek(pdf, y);
  y = renderAssetsInventory(pdf, y, [], []);
  y = renderHeirsNotification(pdf, y, []);
  y = renderCourtForms(pdf, y, stateData?.forms || [], stateData?.name);
  y = renderTaxFinance(pdf, y);
  y = renderFinalDistribution(pdf, y);

  // CTA page
  pdf.addPage();
  drawPageHeader(pdf, "Executor's Probate Checklist");
  const w = pdf.internal.pageSize.getWidth();
  pdf.setFillColor(...BRAND_PRIMARY);
  pdf.rect(PAGE_MARGIN, 30, w - PAGE_MARGIN * 2, 80, 'F');
  pdf.setFont('times', 'bold');
  pdf.setFontSize(20);
  pdf.setTextColor(255, 255, 255);
  pdf.text('Named Executor?', w / 2, 55, { align: 'center' });
  pdf.setFont('times', 'italic');
  pdf.setFontSize(14);
  pdf.setTextColor(...BRAND_ACCENT);
  pdf.text("Here's what to do next.", w / 2, 65, { align: 'center' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(11);
  pdf.setTextColor(255, 255, 255);
  const cta =
    'EstateProFiler turns this checklist into an interactive workspace — ' +
    'state-specific tasks, court-ready PDFs, and a calm interface for ' +
    'first-time executors.';
  pdf.text(cta, w / 2, 82, { align: 'center', maxWidth: w - 60 });
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.setTextColor(255, 255, 255);
  pdf.text('Start your free 14-day trial at estateprofiler.com', w / 2, 100, { align: 'center' });

  paginate(pdf);
  pdf.save(fileName);
}

/* ------------------------------------------------------------------
 * PERSONALIZED checklist — pulls live estate data
 * ------------------------------------------------------------------ */

export function generatePersonalizedChecklist(ctx, preview = false) {
  const {
    estate = {},
    assets = [],
    debts = [],
    heirs = [],
    tasks = [],
    executorContacts = [],
    stateData = null, // optional — caller may inject the live state guidance
    showPreview,
  } = ctx;

  const pdf = new jsPDF({ unit: 'mm', format: 'letter' });

  const estateLine = [
    estate.estate_name && `Estate of ${estate.decedent_name || estate.estate_name}`,
    estate.state && `State: ${estate.state}`,
    estate.date_of_death && `Date of Death: ${fmtDate(estate.date_of_death)}`,
  ]
    .filter(Boolean)
    .join('   ·   ');

  drawCoverPage(
    pdf,
    "The Executor's Probate Checklist",
    estate.estate_name || 'Estate of ' + (estate.decedent_name || ''),
    estateLine || 'Personalized Executor Workbook'
  );

  pdf.addPage();
  drawPageHeader(pdf, "Executor's Probate Checklist");

  let y = 18;
  y = renderProbateSteps(pdf, y, stateData?.steps || [], stateData);
  y = renderFirstWeek(pdf, y);
  y = renderAssetsInventory(pdf, y, assets, debts);
  y = renderHeirsNotification(pdf, y, heirs);
  y = renderCourtForms(pdf, y, stateData?.forms || [], stateData?.name);
  y = renderTaxFinance(pdf, y);
  y = renderFinalDistribution(pdf, y);
  y = renderTasksProgress(pdf, y, tasks);
  y = renderContacts(pdf, y, executorContacts);

  paginate(pdf);

  const fileName = `${(estate.estate_name || 'estate').replace(/\s+/g, '_')}_probate_checklist_${new Date().toISOString().split('T')[0]}.pdf`;

  if (preview && typeof showPreview === 'function') {
    showPreview(pdf, fileName, "Executor's Probate Checklist");
  } else {
    pdf.save(fileName);
  }
}
